/**
 * atomberg-warranty-wizard.js
 *
 * Screen navigation for the Register Warranty wizard
 * (snippets/atomberg-warranty-form.liquid): welcome → phone → products →
 * invoice → purchase → details → review → success. Owns per-screen
 * validation, the progress bar, review-summary rendering, edit-and-return,
 * the ?bg_serialnumber=/?serialnumber=/?serial= deep link, and hiding the
 * Name field when a verified
 * Salesforce lookup already supplied it. Submission itself stays in
 * atomberg-support-forms.js — this file only listens for its
 * 'atomberg:support-form-result' event to route to the success screen (or
 * surface the failure on review).
 *
 * Also fires the Warranty Registration tracking plan's screen-level events
 * (clicked_register_your_warranty, Warranty_mobile_number_entered, the three
 * per-screen Continue events, Entered_Name/_Pincode/_Purchase_Date/
 * _Source_of_Purchase, Uploaded_Invoice, View_Warranty_Review_Page) via
 * fireWarrantyEvent(), dispatched as one atomberg:warranty-event CustomEvent
 * that atomberg-gtm-datalayer.js and atomberg-moengage-datalayer.js both
 * consume generically. Exposed as window.AtombergWarrantyAnalytics.fire so
 * atomberg-support-repeatable.js's per-product-row events (Clicked_Add_
 * Another_Product, entered_serial_number, entered_model_name) can use the
 * same pipe.
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergWarrantyWizardInit) return;
  w._atombergWarrantyWizardInit = true;

  // This flow is the A/B control; the new flow tags its GTM events "v2", so GTM can tell the two apart.
  var WARRANTY_VARIANT = 'v1';

  /**
   * Fires atomberg:warranty-event, the one CustomEvent both
   * atomberg-gtm-datalayer.js and atomberg-moengage-datalayer.js listen for
   * generically — a single dispatch point instead of one named listener per
   * event in each of those two files. event_date_time/page_url are attached
   * centrally by those consumers, not repeated at every call site. UTMs are
   * read fresh here (lt_* localStorage keys, the convention every other
   * tracked event in this theme already uses) so callers never repeat it.
   * Exposed on window so atomberg-support-repeatable.js (per-row product
   * events, shared with Support's own unrelated forms) can call it too.
   * @param {string} name
   * @param {Record<string, any>} payload
   */
  function fireWarrantyEvent(name, payload) {
    /** @param {string} key */
    function utm(key) {
      try {
        return localStorage.getItem('lt_' + key) || '';
      } catch (_) {
        return '';
      }
    }
    document.dispatchEvent(
      new CustomEvent('atomberg:warranty-event', {
        detail: {
          name: name,
          payload: Object.assign(
            {
              utm_source: utm('utm_source'),
              utm_medium: utm('utm_medium'),
              utm_campaign: utm('utm_campaign'),
            },
            payload
          ),
          // Beside the payload, not in it: only the GTM listener reads it, so MoEngage's events stay unchanged.
          warranty_variant: WARRANTY_VARIANT,
        },
        bubbles: true,
      })
    );
  }
  w.AtombergWarrantyAnalytics = { fire: fireWarrantyEvent, variant: WARRANTY_VARIANT };

  // welcome and success are excluded from the progress bar — it tracks the
  // form screens only. Phone comes first so a verified number can prefill
  // the later screens from Salesforce; invoice/date/source share the
  // "purchase" screen, name/pincode close the flow as "details".
  var SCREENS = ['welcome', 'phone', 'products', 'purchase', 'details', 'review', 'success'];
  var PROGRESS_SCREENS = ['phone', 'products', 'purchase', 'details', 'review'];

  /** @param {HTMLElement} root */
  function init(root) {
    var backBtn = /** @type {HTMLButtonElement|null} */ (
      root.querySelector('[data-warranty-back]')
    );
    var progress = /** @type {HTMLElement|null} */ (root.querySelector('[data-warranty-progress]'));
    var progressFill = /** @type {HTMLElement|null} */ (
      root.querySelector('[data-warranty-progress-fill]')
    );

    var current = 'welcome';
    // Set when the user jumps back via a review "Edit" button, so the next
    // successful Continue returns to review instead of marching forward.
    var returnTo = '';
    // Suppresses the scroll-into-view on the very first render — jumping the
    // viewport to the section on page load would hijack the scroll position.
    var booted = false;

    /** @returns {string} */
    function getPhone() {
      return fieldValue('[data-support-otp-phone]');
    }

    /** @param {string} selector @returns {string} */
    function fieldValue(selector) {
      var el = /** @type {HTMLInputElement|HTMLSelectElement|null} */ (
        root.querySelector(selector)
      );
      return el ? el.value.trim() : '';
    }

    /** @param {string} name */
    function screenEl(name) {
      return /** @type {HTMLElement|null} */ (
        root.querySelector('[data-warranty-screen="' + name + '"]')
      );
    }

    /** @param {string} name */
    function show(name) {
      SCREENS.forEach(function (s) {
        var el = screenEl(s);
        if (el) el.hidden = s !== name;
      });
      current = name;

      var progressIndex = PROGRESS_SCREENS.indexOf(name);
      if (progress) progress.hidden = progressIndex === -1;
      if (progressFill && progressIndex !== -1) {
        progressFill.style.width =
          Math.round(((progressIndex + 1) / PROGRESS_SCREENS.length) * 100) + '%';
      }
      if (backBtn) backBtn.hidden = name === 'welcome' || name === 'success';

      if (name === 'details') toggleNameField();
      if (name === 'review') {
        renderReview();
        fireWarrantyEvent('View_Warranty_Review_Page', {
          phone_number: getPhone(),
          name: fieldValue('[name="name"]'),
          pincode: fieldValue('[name="pincode"]'),
        });
      }
      if (booted) {
        // After the rAF so the new screen's layout has settled — scrolling
        // before the swap leaves the viewport clamped near the old (taller)
        // screen's bottom, landing the user below the fields.
        window.requestAnimationFrame(function () {
          var top = root.getBoundingClientRect().top + window.pageYOffset;
          // cushion so the wizard top clears the sticky site header
          window.scrollTo({ top: Math.max(top - 96, 0), behavior: 'smooth' });
        });
      }
      booted = true;
    }

    // A verified Salesforce lookup that already supplied the name means the
    // user has nothing to add — hide the field entirely (the prefilled value
    // still submits). Skipped/unknown numbers see it and type it manually.
    function toggleNameField() {
      var wrapper = /** @type {HTMLElement|null} */ (
        root.querySelector('[data-warranty-name-field]')
      );
      var nameInput = /** @type {HTMLInputElement|null} */ (root.querySelector('[name="name"]'));
      if (!wrapper || !nameInput) return;
      var otp = root.querySelector('[data-support-otp]');
      var verified = !!otp && otp.getAttribute('data-support-otp-verified') === 'true';
      wrapper.hidden = verified && !!nameInput.value;
    }

    /**
     * @param {HTMLElement} screen
     * @param {string} message
     */
    function showScreenError(screen, message) {
      var el = /** @type {HTMLElement|null} */ (
        screen.querySelector('[data-warranty-screen-error]')
      );
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    /**
     * @param {HTMLElement} screen
     * @returns {boolean}
     */
    function validateScreen(screen) {
      // Shared with the Support forms — writes a message under each failing
      // field instead of one line above Continue. atomberg-support-forms.js
      // is loaded before this file, so the namespace is always present.
      var fieldErrors = /** @type {any} */ (window).AtombergFieldErrors;
      if (fieldErrors && !fieldErrors.show(screen)) {
        showScreenError(screen, '');
        return false;
      }
      if (screen.getAttribute('data-warranty-screen') === 'products') {
        var repeatable = screen.querySelector('[data-support-repeatable]');
        if (repeatable && repeatable.getAttribute('data-support-repeatable-invalid') === 'true') {
          showScreenError(screen, 'Please resolve the duplicate serial numbers before continuing.');
          return false;
        }
      }
      if (screen.getAttribute('data-warranty-screen') === 'phone') {
        var otp = screen.querySelector('[data-support-otp]');
        var verified = !!otp && otp.getAttribute('data-support-otp-verified') === 'true';
        var skipped = root.getAttribute('data-warranty-otp-skipped') === 'true';
        if (!verified && !skipped) {
          showScreenError(
            screen,
            'Please verify your mobile number, or choose "Skip verification" to continue.'
          );
          return false;
        }
        // Dispatched here, not just in the skip button's own click handler,
        // because this runs on EVERY successful attempt to leave this screen
        // — including the user going back after skipping once (the skip
        // button stays disabled/skipped=true forever) and re-entering a
        // different number before hitting the generic Next button. Reading
        // the field fresh each time keeps MoEngage's identity in sync with
        // whatever number actually ends up on the submission, not just the
        // one that was in the field the first time Skip was clicked.
        if (!verified && skipped) {
          var phoneInput = /** @type {HTMLInputElement|null} */ (
            screen.querySelector('[data-support-otp-phone]')
          );
          document.dispatchEvent(
            new CustomEvent('atomberg:warranty-otp-skipped', {
              detail: { mobile: phoneInput ? phoneInput.value.trim() : '' },
              bubbles: true,
            })
          );
        }
      }
      showScreenError(screen, '');
      return true;
    }

    function renderReview() {
      var rows = root.querySelectorAll('[data-support-repeatable-row]');
      var products = Array.from(rows)
        .map(function (row) {
          var serial = /** @type {HTMLInputElement|null} */ (
            row.querySelector('[data-support-row-serial]')
          );
          var model = /** @type {HTMLInputElement|null} */ (
            row.querySelector('[data-support-row-model]')
          );
          var label = model && model.value ? model.value : '';
          return (label ? label + ' · ' : '') + (serial ? serial.value : '');
        })
        .filter(Boolean);

      var countEl = root.querySelector('[data-warranty-review-count]');
      if (countEl)
        countEl.textContent = products.length + (products.length === 1 ? ' product' : ' products');
      var productsEl = root.querySelector('[data-warranty-review-products]');
      if (productsEl) productsEl.textContent = products.join(', ') || '—';

      var phoneInput = /** @type {HTMLInputElement|null} */ (
        root.querySelector('[data-support-otp-phone]')
      );
      var nameInput = /** @type {HTMLInputElement|null} */ (root.querySelector('[name="name"]'));
      var phoneEl = root.querySelector('[data-warranty-review-phone]');
      if (phoneEl) {
        var phoneText = phoneInput && phoneInput.value ? phoneInput.value : '—';
        var nameText = nameInput && nameInput.value ? nameInput.value + ' · ' : '';
        phoneEl.textContent = nameText + phoneText;
      }

      var dateInput = /** @type {HTMLInputElement|null} */ (
        root.querySelector('[name="purchase_date"]')
      );
      var sourceSelect = /** @type {HTMLSelectElement|null} */ (
        root.querySelector('[data-support-purchase-source]')
      );
      var sourceOption =
        sourceSelect && sourceSelect.selectedIndex > 0
          ? sourceSelect.options[sourceSelect.selectedIndex]
          : null;
      var sourceLabel = (sourceOption && sourceOption.textContent) || '';
      var purchaseEl = root.querySelector('[data-warranty-review-purchase]');
      if (purchaseEl) {
        purchaseEl.textContent =
          (dateInput && dateInput.value ? dateInput.value : '—') +
          (sourceLabel ? ' · ' + sourceLabel.trim() : '');
      }

      var fileInput = /** @type {HTMLInputElement|null} */ (
        root.querySelector('[data-support-row-file]')
      );
      var invoiceFile = fileInput && fileInput.files ? fileInput.files[0] : null;
      var invoiceEl = root.querySelector('[data-warranty-review-invoice]');
      if (invoiceEl) {
        invoiceEl.textContent = invoiceFile
          ? 'Invoice: ' + invoiceFile.name
          : 'No invoice attached';
      }
    }

    /**
     * Fires the tracking-plan's per-screen "Continue" event. Products can
     * carry multiple items under one invoice, so its per-row attributes
     * (Serial Number/Model Name) are arrays, one entry per row in row
     * order — NOT just the first row, which is what this used to do; Total
     * Products in Form carries the real count regardless.
     * @param {HTMLElement} screen
     */
    function fireContinueEvent(screen) {
      var screenName = screen.getAttribute('data-warranty-screen');
      if (screenName === 'products') {
        var rows = /** @type {HTMLElement[]} */ (
          Array.from(root.querySelectorAll('[data-support-repeatable-row]'))
        );
        var serialNumbers = rows.map(function (row) {
          var el = /** @type {HTMLInputElement|null} */ (
            row.querySelector('[data-support-row-serial]')
          );
          return el ? el.value.trim() : '';
        });
        var modelNames = rows.map(function (row) {
          var el = /** @type {HTMLInputElement|null} */ (
            row.querySelector('[data-support-row-model]')
          );
          return el ? el.value.trim() : '';
        });
        var firstRow = rows[0];
        fireWarrantyEvent('Clicked_Continue_Serial_Number', {
          input_method:
            firstRow && firstRow.getAttribute('data-support-row-serial-source') === 'scanned'
              ? 'Scanned'
              : 'Manual',
          phone_number: getPhone(),
          total_products_in_form: rows.length,
          serial_number: serialNumbers,
          model_name: modelNames,
        });
      } else if (screenName === 'purchase') {
        fireWarrantyEvent('Click_continue_Purchase_details', {
          phone_number: getPhone(),
          source_of_purchase: fieldValue('[data-support-purchase-source]'),
        });
      } else if (screenName === 'details') {
        fireWarrantyEvent('Clicked_Continue_Your_Details', {
          phone_number: getPhone(),
          name: fieldValue('[name="name"]'),
          pincode: fieldValue('[name="pincode"]'),
        });
      }
    }

    root.addEventListener('click', function (e) {
      var target = /** @type {HTMLElement} */ (e.target);
      if (!target || !target.closest) return;

      if (target.closest('[data-warranty-start]')) {
        fireWarrantyEvent('clicked_register_your_warranty', {});
        show('phone');
        return;
      }

      var nextBtn = target.closest('[data-warranty-next]');
      if (nextBtn) {
        var screen = /** @type {HTMLElement|null} */ (nextBtn.closest('[data-warranty-screen]'));
        if (!screen || !validateScreen(screen)) return;
        fireContinueEvent(screen);
        advance();
        return;
      }

      var gotoBtn = target.closest('[data-warranty-goto]');
      if (gotoBtn) {
        returnTo = current;
        show(gotoBtn.getAttribute('data-warranty-goto') || 'products');
        return;
      }

      if (target.closest('[data-warranty-back]')) {
        if (returnTo) {
          var back = returnTo;
          returnTo = '';
          show(back);
          return;
        }
        var idx = SCREENS.indexOf(current);
        show(SCREENS[Math.max(idx - 1, 0)] || 'welcome');
        return;
      }

      if (target.closest('[data-warranty-restart]')) {
        window.location.reload();
      }
    });

    // Moves forward from the current screen — or back to review when the
    // user got here via an Edit button.
    function advance() {
      if (returnTo) {
        var destination = returnTo;
        returnTo = '';
        show(destination);
        return;
      }
      var index = SCREENS.indexOf(current);
      show(SCREENS[Math.min(index + 1, SCREENS.length - 1)] || 'welcome');
    }

    // Explicitly opting out of OTP is the alternative path — the user then
    // types name/pincode manually and the payload carries otp_verified:false.
    // Both paths advance immediately: no extra Continue tap after choosing.
    root.addEventListener('click', function (e) {
      var target = /** @type {HTMLElement} */ (e.target);
      if (!target || !target.closest || !target.closest('[data-warranty-otp-skip]')) return;
      var skipBtn = /** @type {HTMLButtonElement} */ (target.closest('[data-warranty-otp-skip]'));
      root.setAttribute('data-warranty-otp-skipped', 'true');
      skipBtn.textContent = 'Verification skipped';
      skipBtn.disabled = true;
      fireWarrantyEvent('Warranty_mobile_number_entered', {
        phone_number: getPhone(),
        verification: 'Skipped',
      });
      var phoneScreen = screenEl('phone');
      // Skipping still needs the number itself (the payload requires it) —
      // validateScreen enforces the 10-digit field before moving on, and
      // dispatches atomberg:warranty-otp-skipped itself (see there — it's
      // called on every attempt to leave this screen, not just this first one).
      if (phoneScreen && validateScreen(phoneScreen) && current === 'phone') advance();
    });

    // A successful OTP verification is an explicit "continue" — advance
    // without requiring another tap (the Salesforce prefill runs in the
    // background and lands on the later screens).
    root.addEventListener('atomberg:support-otp-changed', function (e) {
      var detail = /** @type {CustomEvent} */ (e).detail || {};
      if (!detail.verified || current !== 'phone') return;
      fireWarrantyEvent('Warranty_mobile_number_entered', {
        phone_number: detail.mobile || getPhone(),
        verification: 'Verified',
      });
      var phoneScreen = screenEl('phone');
      if (phoneScreen) showScreenError(phoneScreen, '');
      advance();
    });

    // Salesforce prefill after OTP verify lives in atomberg-support-otp.js
    // (shared with the Support forms) — nothing wizard-specific needed here.

    root.addEventListener('atomberg:support-form-result', function (e) {
      var res = /** @type {CustomEvent} */ (e).detail || {};
      if (res.status) {
        // One registration can carry multiple products under one invoice —
        // results is the backend's authoritative per-product outcome
        // (confirmed serial_number + warranty_id); model_name has no
        // backend echo, so it's paired in from the still-present row DOM at
        // the same index, matching the order products were submitted in.
        var results = /** @type {Array<{serial_number: string, warranty_id?: string}>} */ (
          Array.isArray(res.results) ? res.results : []
        );
        var rows = /** @type {HTMLElement[]} */ (
          Array.from(root.querySelectorAll('[data-support-repeatable-row]'))
        );
        fireWarrantyEvent('Warranty_Registration_Successful', {
          phone_number: getPhone(),
          name: fieldValue('[name="name"]'),
          pincode: fieldValue('[name="pincode"]'),
          case_id: res.case_id || '',
          purchase_date: fieldValue('[name="purchase_date"]'),
          source_of_purchase: fieldValue('[data-support-purchase-source]'),
          source_of_purchase_other: fieldValue('[name="source_of_purchase_other"]'),
          total_products_in_form: results.length,
          serial_number: results.map(function (r) {
            return r.serial_number || '';
          }),
          warranty_id: results.map(function (r) {
            return r.warranty_id || '';
          }),
          model_name: results.map(function (_r, i) {
            var row = rows[i];
            var el = row
              ? /** @type {HTMLInputElement|null} */ (row.querySelector('[data-support-row-model]'))
              : null;
            return el ? el.value.trim() : '';
          }),
        });
        show('success');
        return;
      }
      var reviewScreen = screenEl('review');
      var errorEl = reviewScreen
        ? /** @type {HTMLElement|null} */ (reviewScreen.querySelector('[data-support-form-error]'))
        : null;
      if (errorEl) {
        errorEl.textContent = res.message || 'Something went wrong. Please try again.';
        errorEl.hidden = false;
      }
    });

    // Warranty deliberately collects no city/state, but a mistyped pincode
    // is this form's easiest typo — blur runs a lookup purely for an inline
    // hint. Non-blocking and fail-open: network/API failures stay silent;
    // this is a courtesy check, not a serviceability gate like
    // installation's.
    var pincodeInput = /** @type {HTMLInputElement|null} */ (
      root.querySelector('[name="pincode"]')
    );
    var pincodeWarning = /** @type {HTMLElement|null} */ (
      root.querySelector('[data-warranty-pincode-warning]')
    );
    if (pincodeInput && pincodeWarning) {
      var pinInput = pincodeInput;
      var pinWarning = pincodeWarning;
      var pincodeSeq = 0;
      pinInput.addEventListener('input', function () {
        pinWarning.hidden = true;
      });
      pinInput.addEventListener('blur', function () {
        var pincode = pinInput.value.trim();
        if (pincode) {
          fireWarrantyEvent('Entered_Pincode', {
            phone_number: getPhone(),
            name: fieldValue('[name="name"]'),
            pincode: pincode,
          });
        }
        if (!/^\d{6}$/.test(pincode)) return;
        var apiRoot = root.closest('[data-support-api-base-url]');
        var apiBaseUrl = (apiRoot && apiRoot.getAttribute('data-support-api-base-url')) || '';
        if (!apiBaseUrl) return;
        var seq = ++pincodeSeq;
        fetch(apiBaseUrl + '/api/support/pincode-lookup?pincode=' + encodeURIComponent(pincode), {
          headers: { Accept: 'application/json' },
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (res) {
            // Only the latest lookup for the still-current value may warn.
            if (seq !== pincodeSeq || pinInput.value.trim() !== pincode) return;
            if (res && res.status === false) {
              pinWarning.textContent = "This pincode isn't recognized — please double-check it.";
              pinWarning.hidden = false;
            } else {
              pinWarning.hidden = true;
            }
          })
          .catch(function () {
            // Courtesy check only — silence is the correct failure mode.
          });
      });
    }

    var nameInputEl = /** @type {HTMLInputElement|null} */ (root.querySelector('[name="name"]'));
    if (nameInputEl) {
      nameInputEl.addEventListener('blur', function (e) {
        var value = /** @type {HTMLInputElement} */ (e.target).value.trim();
        if (!value) return;
        fireWarrantyEvent('Entered_Name', { phone_number: getPhone(), name: value });
      });
    }

    var invoiceInputEl = /** @type {HTMLInputElement|null} */ (
      root.querySelector('[data-warranty-screen="purchase"] [data-support-row-file]')
    );
    if (invoiceInputEl) {
      invoiceInputEl.addEventListener('change', function (e) {
        var target = /** @type {HTMLInputElement} */ (e.target);
        var file = target.files && target.files[0];
        if (!file) return;
        // MIME type, not extension, since that's what the shared file-input
        // validation in atomberg-support-repeatable.js already gates on.
        var fileType = (file.type.split('/')[1] || file.type).toUpperCase();
        fireWarrantyEvent('Uploaded_Invoice', { phone_number: getPhone(), file_type: fileType });
      });
    }

    var purchaseDateInputEl = /** @type {HTMLInputElement|null} */ (
      root.querySelector('[name="purchase_date"]')
    );
    if (purchaseDateInputEl) {
      purchaseDateInputEl.addEventListener('change', function (e) {
        var value = /** @type {HTMLInputElement} */ (e.target).value;
        if (!value) return;
        fireWarrantyEvent('Entered_Purchase_Date', {
          phone_number: getPhone(),
          purchase_date: value,
        });
      });
    }

    var sourceSelectInputEl = /** @type {HTMLSelectElement|null} */ (
      root.querySelector('[data-support-purchase-source]')
    );
    if (sourceSelectInputEl) {
      sourceSelectInputEl.addEventListener('change', function (e) {
        var value = /** @type {HTMLSelectElement} */ (e.target).value;
        if (!value) return;
        fireWarrantyEvent('Entered_Source_of_Purchase', {
          phone_number: getPhone(),
          source_of_purchase: value,
        });
      });
    }

    var params = new URLSearchParams(window.location.search);
    // bg_serialnumber is what the QR sticker on the product box encodes
    // (reference-site vocabulary); serialnumber and serial are accepted as
    // manual/short aliases. First non-empty wins.
    var deepLinkSerial = (
      params.get('bg_serialnumber') ||
      params.get('serialnumber') ||
      params.get('serial') ||
      ''
    ).trim();
    if (deepLinkSerial) {
      var firstSerial = /** @type {HTMLInputElement|null} */ (
        root.querySelector('[data-support-row-serial]')
      );
      if (firstSerial) {
        firstSerial.value = deepLinkSerial;
        // input + blur so the repeatable engine validates and runs its
        // serial lookup exactly as if the user had typed it.
        firstSerial.dispatchEvent(new Event('input', { bubbles: true }));
        firstSerial.dispatchEvent(new Event('blur', { bubbles: true }));
      }
      // Serial is pre-filled but the flow still starts at the phone step —
      // it drives the Salesforce prefill for everything after.
      show('phone');
    } else {
      show('welcome');
    }
  }

  function initAll() {
    document.querySelectorAll('[data-warranty-wizard]').forEach(function (el) {
      var root = /** @type {HTMLElement} */ (el);
      if (/** @type {any} */ (root)._warrantyWizardBound) return;
      /** @type {any} */ (root)._warrantyWizardBound = true;
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
